// AUPRÉA — Parcours d'intégration : frise 7 étapes + espace recrue
import { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import axios from "axios";
import { C, Btn, Spinner, Alert } from "./shared";
import { SignatureContrats } from "./ContratsSignature";
import { PaiementCotisation } from "./CotisationPaiement";

import { API, hdrs, TEAL, OR, ROUGE, fr, errMsg } from "./integrationShared";
export { API, hdrs, TEAL, OR, ROUGE, fr, errMsg };

const pastille = (s, size) => {
  const base = { width: size, height: size, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.5, fontWeight: 700, flexShrink: 0, boxSizing: "border-box" };
  if (s === "terminee") return { ...base, background: TEAL, color: "#fff" };
  if (s === "en_cours") return { ...base, background: OR, color: "#fff" };
  if (s === "attente_auprea") return { ...base, background: OR, color: "#fff" };
  if (s === "refusee") return { ...base, background: ROUGE, color: "#fff" };
  return { ...base, background: "#fff", border: `2px solid ${TEAL}55`, color: C.muted };
};
const icone = (s, i) => (s === "terminee" ? "✓" : s === "attente_auprea" ? "⏳" : s === "refusee" ? "!" : i + 1);

const FRISE_CSS = `.aup-frise{display:flex;align-items:flex-start;position:relative}
.aup-frise-step{flex:1;display:flex;flex-direction:column;align-items:center;text-align:center;position:relative;min-width:0;padding:0 4px}
.aup-frise-step:not(:last-child)::after{content:"";position:absolute;top:var(--half);left:calc(50% + var(--half));right:calc(-50% + var(--half));height:3px;background:${TEAL};opacity:.35}
.aup-frise-step.done:not(:last-child)::after{opacity:1}
@media (max-width:720px){.aup-frise:not(.mini){flex-direction:column;gap:14px}.aup-frise:not(.mini) .aup-frise-step{flex-direction:row;text-align:left;gap:12px;align-items:flex-start}
.aup-frise:not(.mini) .aup-frise-step::after{display:none}}`;

const MotifEtape = ({ e, testid }) => {
  const [open, setOpen] = useState(false);
  const liste = e.pieces_refusees || [];
  const titre = liste.map(p => `${p.piece} : ${p.motif || "à corriger"}${p.commentaire ? ` (${p.commentaire})` : ""}`).join("\n");
  return (
    <div style={{ fontSize: 10.5, color: ROUGE, marginTop: 2 }}>
      <div data-testid={`${testid}-${e.key}-motif`} title={titre || undefined} onClick={() => liste.length && setOpen(!open)} style={{ cursor: liste.length ? "pointer" : "default", textDecoration: liste.length ? "underline dotted" : "none" }}>{e.motif}</div>
      {open && <div data-testid={`${testid}-${e.key}-pieces-refusees`} style={{ textAlign: "left", background: "#FEF2F2", borderRadius: 6, padding: 6, marginTop: 4 }}>
        {liste.map(p => <div key={p.piece} style={{ marginBottom: 3 }}><strong>{p.piece}</strong> : {p.motif || "à corriger"}{p.commentaire ? ` — ${p.commentaire}` : ""}</div>)}
      </div>}
    </div>
  );
};

export const Frise = ({ etapes, mini = false, testid = "frise" }) => {
  const size = mini ? 16 : 34;
  return (
    <>
      <style>{FRISE_CSS}</style>
      <div className={`aup-frise${mini ? " mini" : ""}`} style={{ "--half": `${size / 2}px` }} data-testid={testid}>
        {etapes.map((e, i) => (
          <div key={e.key} className={`aup-frise-step${e.statut === "terminee" ? " done" : ""}`} data-testid={`${testid}-${e.key}`} data-statut={e.statut}
            title={mini ? `${e.label} · ${e.statut_label}${e.motif ? ` · ${e.motif}` : ""}${e.relances?.length ? ` · 🔔 relancée le ${e.relances.map(fr).join(", ")}` : ""}` : undefined}>
            <div style={pastille(e.statut, size)}>{mini ? (e.statut === "terminee" ? "✓" : "") : icone(e.statut, i)}</div>
            {!mini && (
              <div style={{ marginTop: 8, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: e.statut === "a_venir" ? C.muted : C.navy, lineHeight: 1.3 }}>{e.label}</div>
                <div style={{ fontSize: 10.5, color: e.statut === "refusee" ? ROUGE : e.statut === "terminee" ? TEAL : e.statut === "a_venir" ? C.muted : OR, marginTop: 3 }}>
                  {e.statut === "terminee" ? `✓ ${fr(e.at)}` : e.statut === "a_venir" ? "À venir" : e.statut_label}
                </div>
                {e.sous_titre && <div style={{ fontSize: 10.5, color: C.mid, marginTop: 2 }} data-testid={`${testid}-${e.key}-sous-titre`}>{e.sous_titre}</div>}
                {e.statut === "refusee" && e.motif && <MotifEtape e={e} testid={testid} />}
                {e.relances?.length > 0 && <div style={{ fontSize: 10.5, color: OR, marginTop: 2, display: "flex", gap: 3, alignItems: "center", justifyContent: "inherit", flexWrap: "wrap" }} data-testid={`${testid}-${e.key}-relances`}
                  title={`Relances : ${e.relances.map(fr).join(", ")}`}><Bell size={11} /> {e.relances.map(fr).join(" · ")}</div>}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
};

const PIECE_COL = { manquante: [C.mid, "À déposer"], a_controler: [OR, "⏳ En contrôle AUPRÉA"], refusee: [ROUGE, "Refusée : à redéposer"], validee: [TEAL, "✓ Validée"] };

export const FichierLien = ({ iid, type, v, f }) => {
  const ouvrir = async () => {
    const r = await axios.get(`${API}/integration/${iid}/pieces/${type}/${v}/${f.n}?apercu=true`, { ...hdrs(), responseType: "blob" });
    window.open(URL.createObjectURL(r.data), "_blank");
  };
  return <button onClick={ouvrir} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 11, padding: 0, textDecoration: "underline" }}
    data-testid={`fichier-${type}-v${v}-${f.n}`}>{f.nom}</button>;
};

const PieceDepot = ({ iid, p, onDone }) => {
  const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const [col, lab] = PIECE_COL[p.statut] || PIECE_COL.manquante;
  const peut = ["manquante", "refusee"].includes(p.statut);
  const envoyer = async (files) => {
    if (!files.length) return;
    const big = [...files].find(f => f.size > 10 * 1024 * 1024);
    if (big) return setErr(`${big.name} dépasse 10 Mo`);
    const fd = new FormData(); [...files].forEach(f => fd.append("fichiers", f));
    setBusy(true); setErr("");
    try { await axios.post(`${API}/integration/moi/pieces/${p.type}`, fd, hdrs()); onDone(); } catch (e) { setErr(errMsg(e)); }
    setBusy(false);
  };
  const last = p.versions[p.versions.length - 1];
  return (
    <div style={{ border: `.5px solid ${C.border}`, borderLeft: `3px solid ${col}`, borderRadius: 10, padding: 14, background: "#fff" }} data-testid={`piece-${p.type}`}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.navy }}>{p.label.replace("au nom de la recrue", "à votre nom")} {!p.obligatoire && <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>(facultative)</span>}</div>
          <div style={{ fontSize: 11, color: col, marginTop: 3 }} data-testid={`piece-${p.type}-statut`}>{lab}{p.versions.length > 1 ? ` · version ${p.versions.length}` : ""}</div>
          {p.statut === "refusee" && <div style={{ fontSize: 11, color: ROUGE, marginTop: 3 }} data-testid={`piece-${p.type}-motif`}>Motif : {(p.motifs || []).join(", ")}{p.commentaire ? ` — ${p.commentaire}` : ""}</div>}
          {last && <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>{last.fichiers.map(f => <FichierLien key={f.n} iid={iid} type={p.type} v={last.v} f={f} />)}</div>}
        </div>
        {peut && (
          <label style={{ background: OR, color: "#fff", padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: busy ? "wait" : "pointer" }}>
            {busy ? "Envoi…" : p.statut === "refusee" ? "Redéposer" : "Déposer"}
            <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.heic,.heif,image/*,application/pdf" hidden disabled={busy}
              onChange={(e) => envoyer(e.target.files)} data-testid={`piece-${p.type}-input`} />
          </label>
        )}
      </div>
      {err && <div style={{ fontSize: 11, color: ROUGE, marginTop: 6 }} data-testid={`piece-${p.type}-erreur`}>{err}</div>}
    </div>
  );
};

const Bloc = ({ titre, children, testid }) => (
  <div style={{ background: "#fff", border: `.5px solid ${C.border}`, borderRadius: 14, padding: 22, marginTop: 18 }} data-testid={testid}>
    <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 18, color: C.navy, marginBottom: 12 }}>{titre}</div>
    {children}
  </div>
);

const Cotisation = ({ d, reload }) => {
  const e = d.frise.find(x => x.key === "cotisation");
  if (e.statut === "terminee") return <div style={{ fontSize: 13, color: TEAL }} data-testid="cotisation-ok">✓ {e.sous_titre === "Terminée — exonéré" ? "Terminée — exonéré (Partenaire recruteur)" : `1re cotisation reçue${e.sous_titre ? ` · ${e.sous_titre}` : ""}`}</div>;
  if (e.statut === "a_venir") return <div style={{ fontSize: 12, color: C.muted }}>Disponible après la création de votre espace.</div>;
  return <PaiementCotisation onDone={reload} />;
};

export const EspaceIntegration = () => {
  const [d, setD] = useState(null); const [err, setErr] = useState("");
  const load = () => axios.get(`${API}/integration/moi`, hdrs()).then(r => setD(r.data)).catch(e => setErr(errMsg(e)));
  useEffect(() => { load(); }, []);
  if (err) return <Alert type="info"><span>ℹ</span><span>{err}. Contactez contact@auprea.com.</span></Alert>;
  if (!d) return <Spinner />;
  const contrats = d.frise.find(x => x.key === "contrats");
  return (
    <div style={{ maxWidth: 980, margin: "0 auto" }} data-testid="espace-integration">
      <div style={{ background: C.navy, color: "#fff", padding: "26px 28px", borderRadius: 16 }}>
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 28 }}>Bienvenue, {d.prenom}</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,.75)", marginTop: 6 }}>
          {d.termine ? "Votre intégration est terminée : votre accès complet est ouvert." : "Votre espace intégration AUPRÉA. Le reste de la plateforme s'ouvrira automatiquement à la fin du parcours."}
        </div>
      </div>
      <Bloc titre="Mon parcours" testid="bloc-frise"><Frise etapes={d.frise} /></Bloc>
      <Bloc titre="Mes pièces justificatives" testid="bloc-pieces">
        <div style={{ fontSize: 11.5, color: C.mid, marginBottom: 12 }}>{d.formats} · stockage privé chiffré, visible uniquement par vous et AUPRÉA. Pour la pièce d'identité, déposez le recto et le verso ensemble.</div>
        <div style={{ display: "grid", gap: 10 }}>{d.pieces.map(p => <PieceDepot key={p.type} iid={d.id} p={p} onDone={load} />)}</div>
      </Bloc>
      <Bloc titre="Mes contrats" testid="bloc-contrats">
        {contrats.statut === "a_venir"
          ? <div style={{ fontSize: 13, color: C.mid }} data-testid="contrats-statut">Disponible après la création de votre espace. Vous pourrez signer sans attendre la validation de vos pièces.</div>
          : <>{contrats.statut === "terminee" && <div style={{ fontSize: 13, color: TEAL, marginBottom: 10 }} data-testid="contrats-statut">✓ {contrats.sous_titre || "Contrats signés"}</div>}<SignatureContrats onDone={load} /></>}
      </Bloc>
      <Bloc titre="Ma cotisation" testid="bloc-cotisation"><Cotisation d={d} reload={load} /></Bloc>
    </div>
  );
};
